import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Map as MLMap, Marker, NavigationControl } from "maplibre-gl";
import { setWorkerUrl } from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
setWorkerUrl(maplibreWorkerUrl);
import { MAP_STYLE, applyBasemap, availableBasemaps, type Basemap } from "../lib/basemap";
import BasemapPicker from "../ui/BasemapPicker";
import { iconSvg } from "../lib/icons";
import { MK_OFFSET, markerParts, setSilhouette } from "../lib/vehicleMarker";
import { ago, fijiTime } from "../lib/format";

type Shared = {
  name: string; icon: string | null; expires_at: string;
  position: { lat: number; lng: number; kmh: number; course: number; fix_at: string; seen_at: string } | null;
};
const POLL_MS = 15000;

/** Public, no-login live-location page for an expiring share link: /s/<token> */
export default function SharePage() {
  const { token } = useParams();
  const [data, setData] = useState<Shared | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [basemap, setBasemap] = useState<Basemap>("streets");
  const mapEl = useRef<HTMLDivElement>(null);
  const map = useRef<MLMap | null>(null);
  const marker = useRef<Marker | null>(null);
  const centred = useRef(false);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const r = await fetch(`/api/share/${encodeURIComponent(token ?? "")}`);
        const b = await r.json().catch(() => ({}));
        if (stop) return;
        if (!r.ok) { setErr(b.error ?? "This link is not available."); setData(null); return; }
        setData(b); setErr(null);
      } catch { if (!stop) setErr("Can't reach Tracking Fiji right now — retrying…"); }
    };
    load();
    const t = setInterval(load, POLL_MS);
    const c = setInterval(() => setNow(Date.now()), 5000);
    return () => { stop = true; clearInterval(t); clearInterval(c); };
  }, [token]);

  useEffect(() => {
    if (!mapEl.current || map.current) return;
    const m = new MLMap({ container: mapEl.current, style: MAP_STYLE, center: [178.0, -17.8], zoom: 7, attributionControl: { compact: true } });
    m.addControl(new NavigationControl({ visualizePitch: false }), "top-right");
    map.current = m;
    return () => { m.remove(); map.current = null; };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    applyBasemap(m, basemap);
  }, [basemap]);

  useEffect(() => {
    const m = map.current, p = data?.position;
    if (!m || !p) return;
    if (!marker.current) {
      const el = document.createElement("div");
      el.className = "mk";
      marker.current = new Marker({ element: el, anchor: "left", offset: MK_OFFSET }).setLngLat([p.lng, p.lat]).addTo(m);
    }
    const moving = p.kmh >= 3 && now - Date.parse(p.seen_at) < 15 * 60000;
    const el = marker.current.getElement();
    el.dataset.status = moving ? "moving" : now - Date.parse(p.seen_at) > 15 * 60000 ? "offline" : "stopped";
    const { dot, label } = markerParts(el);
    setSilhouette(dot, data!.icon, p.course, p.kmh);
    label!.textContent = `${data!.name} · ${p.kmh} km/h`;
    marker.current.setLngLat([p.lng, p.lat]);
    if (!centred.current) { centred.current = true; m.jumpTo({ center: [p.lng, p.lat], zoom: 14 }); }
    else if (!m.getBounds().contains([p.lng, p.lat])) m.easeTo({ center: [p.lng, p.lat] });
  }, [data, now]);

  const left = data ? Date.parse(data.expires_at) - now : 0;
  const leftTxt = left <= 0 ? "expired" : left < 3600_000 ? `${Math.ceil(left / 60000)} min` : `${Math.floor(left / 3600_000)} h ${Math.round((left % 3600_000) / 60000)} min`;

  return (
    <div className="share-page">
      <header className="topbar">
        <img src="/logo.png" alt="Tracking Fiji" className="topbar-logo" />
        <div className="topbar-spacer" />
        {data && <span className="sub">Link expires in {leftTxt}</span>}
      </header>
      <div className="share-map">
        <div ref={mapEl} className="map" />
        <BasemapPicker value={basemap} options={availableBasemaps(["maptiler"])} onChange={setBasemap} />
        <section className="share-card">
          {err ? (
            <><strong>Live location unavailable</strong><p className="muted">{err}</p></>
          ) : !data ? (
            <p className="muted">Loading…</p>
          ) : (
            <>
              <div className="strip-head">
                <span className="licon moving" dangerouslySetInnerHTML={{ __html: iconSvg(data.icon, 15) }} />
                <strong>{data.name}</strong>
              </div>
              {data.position ? (
                <dl>
                  <dt>Speed</dt><dd className="mono">{data.position.kmh} km/h</dd>
                  <dt>Last update</dt><dd className="mono">{ago(data.position.seen_at, now)}</dd>
                  <dt>Position time</dt><dd className="mono">{fijiTime(data.position.fix_at)}</dd>
                </dl>
              ) : <p className="muted">No position received yet.</p>}
              <p className="fine">Shared via Tracking Fiji · updates every 15 seconds</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
